import { Card, Direction } from '../../state/ArenaTypes';
import { GameTableConfig } from '../../engine/tableConfig/types';
import { areSuitsEqual, normalizeSuit, isRedSuit } from '../../engine/CardSuits';
import { BaseHandSortingStrategy } from './BaseHandSortingStrategy';

const RANK_POWER_STANDARD: Record<string, number> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10,
  'J': 11, 'Q': 12, 'K': 13, 'A': 14
};

/**
 * Standard, game-agnostic hand sorting strategy for traditional trick-taking games.
 * Inherits alternating Black/Red suit ordering from BaseHandSortingStrategy.
 * Orders cards within each suit ascending (2 through Ace).
 * Places active trump cards at the end (far right) of the hand.
 */
export class StandardHandSortingStrategy extends BaseHandSortingStrategy {
  private isCardTrump(card: Card, activeTrump?: string | null): boolean {
    if (!activeTrump || activeTrump === 'NT' || activeTrump === 'NoTrump' || activeTrump === 'NO_TRUMP') {
      return false;
    }
    return areSuitsEqual(card.suit, activeTrump);
  }

  public sortHand(
    hand: Card[],
    _direction?: Direction | string | null,
    trumpSuit?: string | null,
    config?: GameTableConfig
  ): Card[] {
    if (!hand || hand.length === 0) return [];
    const validCards = hand.filter((c): c is Card => Boolean(c && (c.id || c.rank)));
    if (validCards.length === 0) return [];

    const defaultTrump = config?.features?.defaultTrumpSuit ?? null;
    const activeTrump = trumpSuit ?? defaultTrump;
    const normTrump = activeTrump && activeTrump !== 'NT' && activeTrump !== 'NoTrump' && activeTrump !== 'NO_TRUMP'
      ? normalizeSuit(activeTrump)
      : null;

    const trumps: Card[] = [];
    const nonTrumps: Record<string, Card[]> = {
      Clubs: [],
      Diamonds: [],
      Spades: [],
      Hearts: []
    };

    validCards.forEach(c => {
      if (this.isCardTrump(c, activeTrump)) {
        trumps.push(c);
      } else {
        const norm = normalizeSuit(c.suit);
        if (nonTrumps[norm]) {
          nonTrumps[norm].push(c);
        } else {
          nonTrumps.Clubs.push(c);
        }
      }
    });

    const sortFn = (a: Card, b: Card) => (RANK_POWER_STANDARD[a.rank] ?? 0) - (RANK_POWER_STANDARD[b.rank] ?? 0);
    trumps.sort(sortFn);
    Object.values(nonTrumps).forEach(arr => arr.sort(sortFn));

    const presentNonTrumpSuits = ['Clubs', 'Diamonds', 'Spades', 'Hearts'].filter(s => nonTrumps[s].length > 0);
    const orderedSuits = this.getAlternatingSuitOrder(presentNonTrumpSuits, normTrump);

    const orderedNonTrumps: Card[] = [];
    orderedSuits.forEach(suit => {
      if (nonTrumps[suit]) {
        orderedNonTrumps.push(...nonTrumps[suit]);
      }
    });

    return [
      ...orderedNonTrumps,
      ...trumps
    ];
  }
}

